import {inject, Injectable} from "@angular/core";
import { Subject } from "rxjs";
import { filter, map, pairwise } from "rxjs/operators";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import { ProjectService } from '@wms/services';
import {EventManagerService} from "@wms/app";
import {forEach, isEmpty, replace, set, some, takeRight} from "lodash-es";

let LIMIT_BY_CATEGORY: {[key: string]: number} = {
  'PAGE': 9,
  'DB': 3,
  'JAVASERVICE': 3,
  'APISERVICE': 3,
  'RESOURCE': 6
};

let url_prefix = {
  PAGE: {path: '/page/', regex: /\/page\/([^\/?]+)/},
  APISERVICE: {path: '/apis/', regex: /\/apis\/([^\/?]+)/},
  JAVASERVICE: {path: '/java-service/', regex: /\/java-service\/([^\/?]+)/},
  WEBSERVICE: {path: '/web-service/', regex: /\/web-service\/([^\/?]+)/},
  DB: {path: '/database/', regex: /\/database\/([^\/?]+)/}
}

interface ResourceType {
  absPath: string;
  allowDelete: boolean;
  allowEdit: boolean;
  name: string;
  path: string;
  relativePath: string;
  type: string;
}
@Injectable({
  providedIn: 'root'
})
export class RouterHistoryService {
  projectId: string;
  private eventManager: EventManagerService;
  constructor(private router: Router, private projectService: ProjectService, private activatedRoute: ActivatedRoute) {
    this.projectId = this.projectService.getProjectId();
    this.eventManager = inject(EventManagerService);
    // Record changing of all url
    if (sessionStorage.getItem('WMPathInfo')) {
      this.urlData = JSON.parse(sessionStorage.getItem('WMPathInfo') as any);
      this.urlHistory = this.urlData[this.projectId];
      if (this.urlData['metadata_' + this.projectId]) {
        this.urlTypeMap = this.urlData['metadata_' + this.projectId]
        this.urlHistoryMap = this.urlData['history_' + this.projectId]
      }
    }
    this.recordUrlHistory();

    this.eventManager.updateTabOnWsObservable().subscribe((data) => {
      let index = this.urlTypeMap['PAGE'].findIndex((val) => val.split('::')[0] === data.pgName);
      this.urlTypeMap['PAGE'][index] = data.pgName + '::' + data.tab;
    })
  }


  private subjectPrevAndCurrUrl: Subject<PrevAndCurrentUrlInfo> = new Subject<PrevAndCurrentUrlInfo>();
  public prevAndCurrUrl$ = this.subjectPrevAndCurrUrl.asObservable();

  public urlHistory: string[] = [];
  public urlHistoryMap: { [key: string]: Array<any> } = {
    PAGE: [],
    DB: [],
    JAVASERVICE: [],
    APISERVICE: [],
    RESOURCE: [] };
  public urlTypeMap: { [key: string]: Array<any> } = { PAGE: [],
                                                          DB: [],
                                                          JAVASERVICE: [],
                                                          APISERVICE: [],
                                                          RESOURCE: [] };
  private urlData: any = {};

  private _previousUrl: string = '';
  private _currentUrl: string = '';
  public historyEntries: Array<any> = [];

  private firstRecord: boolean = false;

  set previousUrl(url: string) {
    this._previousUrl = url;
  }

  get previousUrl(): string {
    return this._previousUrl;
  }

  set currentUrl(url: string) {
    this._currentUrl = url;
  }

  get currentUrl(): string {
    return this._currentUrl;
  }

  private recordUrlHistory(): void {

    // Quantify initial value for current value
    this.currentUrl = this.router.url;

    // Only works first time of calling this function
    if (!this.firstRecord) {
      this.firstRecord = true;
      if (!this.urlHistory) {
        this.urlHistory = [];
      }
      let currentUrl = this.getCurrentUrl(this.router);
      this.assignUrlType(currentUrl);
      this.urlHistory.push(currentUrl);
      forEach(this.urlHistoryMap, (items: any, key: string) => {
        forEach(items, (item: any) => {
          let entry = this.assignUrlType(item, true);
          this.historyEntries.push(entry);
        });
      });
    }

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        pairwise(),
        map(event => {
          return {
            previousUrl: (event[0] as NavigationEndInfo).url,
            currentUrl: (event[1] as NavigationEndInfo).url,
          } as PrevAndCurrentUrlInfo;
        }),
      )
      .subscribe((response: PrevAndCurrentUrlInfo) => {

        // Update current and previous url
        this.previousUrl = response.previousUrl;
        this.currentUrl = response.currentUrl;

        if (this.currentUrl.includes('/apis/import')) {
          return;
        }
        let entry = this.assignUrlType(this.currentUrl);
        // Broadcast new value of current and previous url value
        this.subjectPrevAndCurrUrl.next(response); //  this can also be moved after if loop. need to check. TODO
        if (isEmpty(entry)) {
          return;
        }

        // Update url history
        this.urlHistory = [...this.urlHistory, response.currentUrl];
        this.urlData[this.projectId] = this.urlHistory;
        this.urlData['metadata_' + this.projectId] = this.urlTypeMap;
        this.urlData['history_' + this.projectId] = this.urlHistoryMap;
        sessionStorage.setItem('WMPathInfo', JSON.stringify(this.urlData));
        this.eventManager.emitRouteHistoryChange(entry);
        this.eventManager.emitSetActiveWs(entry);
      });
  }

  public getCurrentUrl(router: Router): string {
    return router.url;
  }

  public getUrlHistory(): string[] {
    return this.urlHistory;
  }

  private assignUrlType(url: string, add?: boolean) {
    let addedUrlType: any;
    let removed: string = '';
    some(url_prefix, (config: any, type: string) => {
      if (url.startsWith(config.path)) {
        let matches = url.match(config.regex);
        if (matches?.length && matches[1] === 'rest') {
          let regex = /\/apis\/rest\/([^\/?]+)/;
          matches = url.match(regex);
        }
        else if(matches?.length && matches[1] === 'websocket') {
            let regex = /\/apis\/websocket\/([^\/?]+)/;
            matches = url.match(regex);
        }
        if (matches?.length && matches[1]) {
          let index = this.urlTypeMap[type].indexOf(matches[1]);
          if (index > -1) {
            this.urlTypeMap[type].splice(index, 1);
            this.urlTypeMap[type].push(matches[1]);
            if (add) {
              addedUrlType = {type: type, name: matches[1], removed: removed, url: url}
            }
          } else {
            if (this.urlTypeMap[type].length === LIMIT_BY_CATEGORY[type]) {
              // map has reached limit, then removing the last accessed i.e. first item in map.
              removed = this.urlTypeMap[type].splice(0, 1)[0];
            }
            this.urlTypeMap[type].push(matches[1]);
            this.urlHistoryMap[type].push(url);
            addedUrlType = {type: type, name: matches[1], removed: removed, url: url}
          }
        }
        return true;
      }
      return false;
    });
    return addedUrlType;
  }

  removeFromHistory(type: string, name: string, url: string) {
    // @ts-ignore
    this.urlHistoryMap[type]?.forEach((val, index) => {
      if (val && url) {
        if (val === url) {
          this.urlHistoryMap[type][index] = '';
          return true;
        }
      }
    });
    this.urlHistoryMap[type] = this.urlHistoryMap[type].filter(val => val);
    let i = this.urlTypeMap[type].findIndex((val: string) => val === name);
    i > -1 && this.urlTypeMap[type].splice(i, 1);

    this.urlData['metadata_' + this.projectId] = this.urlTypeMap;
    this.urlData['history_' + this.projectId] = this.urlHistoryMap;
    sessionStorage.setItem('WMPathInfo', JSON.stringify(this.urlData));
  }

  setResource(resource: ResourceType, fileRef?: any) {
    set(resource, '_category', 'RESOURCE');
    if (fileRef) {
      set(resource, 'isDirty', () => fileRef.resourceModified);
    }
    this.eventManager.emitRouteHistoryChange(resource);
    this.eventManager.emitSetActiveWs(resource);
  }
}
export interface NavigationEndInfo {
  id: number;
  url: string;
  urlAfterRedirects: string;
}

export interface PrevAndCurrentUrlInfo {
  previousUrl: string;
  currentUrl: string;
}
